import React, { useEffect, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import FileDropZone from 'tools/FileDropZone';
import {
  btnRow,
  card,
  chipStyle,
  errorBox,
  formatMb,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  warnBox,
} from 'tools/toolStyles';
import { downloadBlob, getWin, replaceExt, revokeUrl } from 'tools/ffmpegShared';

type OutFmt = 'png' | 'jpeg' | 'webp';
type TargetId = '500kb' | '1mb' | '2mb';

type ResultItem = {
  id: string;
  name: string;
  blob: any;
  url: string;
  beforeBytes: number;
  afterBytes: number;
  note?: string;
};

const MIME: { [k in OutFmt]: string } = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

const EXT: { [k in OutFmt]: string } = {
  png: 'png',
  jpeg: 'jpg',
  webp: 'webp',
};

const TARGETS: { id: TargetId; label: string; bytes: number }[] = [
  { id: '500kb', label: '500 KB', bytes: 500 * 1024 },
  { id: '1mb', label: '1 MB', bytes: 1024 * 1024 },
  { id: '2mb', label: '2 MB', bytes: 2 * 1024 * 1024 },
];

const SCALES = [1, 0.85, 0.7, 0.55, 0.4, 0.3, 0.2, 0.15];
const QUALITIES = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.22];

function loadImage(file: any): Promise<any> {
  const win = getWin();
  return new Promise((resolve, reject) => {
    const url = win.URL.createObjectURL(file);
    const img = new win.Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      win.URL.revokeObjectURL(url);
      reject(new Error(`Không đọc được ảnh: ${file.name}`));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: any, type: string, quality: number): Promise<any> {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b: any) => resolve(b), type, quality);
    } else {
      try {
        const dataUrl = canvas.toDataURL(type, quality);
        const win = getWin();
        const bin = win.atob(dataUrl.split(',')[1]);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(new win.Blob([arr], { type }));
      } catch {
        resolve(null);
      }
    }
  });
}

function sameFamily(file: any, fmt: OutFmt): boolean {
  const name = (file && file.name ? String(file.name) : '').toLowerCase();
  const type = (file && file.type ? String(file.type) : '').toLowerCase();
  if (fmt === 'jpeg') return type.indexOf('jpeg') >= 0 || type.indexOf('jpg') >= 0 || /\.jpe?g$/.test(name);
  if (fmt === 'png') return type.indexOf('png') >= 0 || /\.png$/.test(name);
  if (fmt === 'webp') return type.indexOf('webp') >= 0 || /\.webp$/.test(name);
  return false;
}

function formatKbOrMb(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${formatMb(bytes)} MB`;
}

async function encodeAt(
  img: any,
  fmt: OutFmt,
  scale: number,
  quality: number
): Promise<{ blob: any; outFmt: OutFmt; note?: string } | null> {
  const win = getWin();
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));
  const canvas = win.document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  if (fmt === 'jpeg' || fmt === 'webp') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(img, 0, 0, w, h);

  let outFmt = fmt;
  let blob = await canvasToBlob(canvas, MIME[fmt], quality);
  let note: string | undefined;
  if (!blob && fmt === 'webp') {
    outFmt = 'png';
    blob = await canvasToBlob(canvas, MIME.png, 1);
    note = 'Trình duyệt không hỗ trợ WebP → đã xuất PNG.';
  }
  if (!blob) return null;
  return { blob, outFmt, note };
}

async function compressOne(file: any, fmt: OutFmt, maxBytes: number): Promise<ResultItem> {
  const win = getWin();
  const beforeBytes = file.size || 0;

  // Already under target and same format → keep original
  if (beforeBytes > 0 && beforeBytes <= maxBytes && sameFamily(file, fmt)) {
    const url = win.URL.createObjectURL(file);
    return {
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      name: file.name,
      blob: file,
      url,
      beforeBytes,
      afterBytes: beforeBytes,
      note: 'Đã dưới mục tiêu — giữ nguyên file.',
    };
  }

  const { img, url } = await loadImage(file);
  try {
    let best: { blob: any; outFmt: OutFmt; note?: string } | null = null;
    let hitTarget = false;
    const qualities = fmt === 'png' ? [1] : QUALITIES;

    for (let si = 0; si < SCALES.length; si++) {
      const scale = SCALES[si];
      for (let qi = 0; qi < qualities.length; qi++) {
        const q = qualities[qi];
        const encoded = await encodeAt(img, fmt, scale, q);
        if (!encoded || !encoded.blob) continue;
        if (!best || encoded.blob.size < best.blob.size) {
          best = encoded;
        }
        if (encoded.blob.size <= maxBytes) {
          hitTarget = true;
          best = encoded;
          // stop early once under target
          si = SCALES.length;
          break;
        }
      }
    }

    if (!best || !best.blob) throw new Error('Không tạo được file đầu ra.');

    const notes: string[] = [];
    if (best.note) notes.push(best.note);
    if (!hitTarget) notes.push(`Chưa đạt mục tiêu ${formatKbOrMb(maxBytes)} — đã giữ bản nhỏ nhất có thể.`);
    else if (best.blob.size < beforeBytes) {
      const saved = Math.round((1 - best.blob.size / beforeBytes) * 100);
      notes.push(`Giảm ~${saved}%`);
    }

    const name = replaceExt(file.name, EXT[best.outFmt]);
    const outUrl = win.URL.createObjectURL(best.blob);
    return {
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      name,
      blob: best.blob,
      url: outUrl,
      beforeBytes,
      afterBytes: best.blob.size,
      note: notes.length ? notes.join(' · ') : undefined,
    };
  } finally {
    revokeUrl(url);
  }
}

const CompressImage = () => {
  const [files, setFiles] = useState<any[]>([]);
  const [fmt, setFmt] = useState<OutFmt>('jpeg');
  const [targetId, setTargetId] = useState<TargetId>('1mb');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<ResultItem[]>([]);

  useEffect(() => {
    return () => {
      results.forEach((r) => revokeUrl(r.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFiles = (list: any[]) => {
    setFiles(list || []);
    setError('');
    results.forEach((r) => revokeUrl(r.url));
    setResults([]);
  };

  const maxBytes = (TARGETS.filter((t) => t.id === targetId)[0] || TARGETS[1]).bytes;

  const run = async () => {
    if (!files.length) {
      setError('Hãy chọn ít nhất một ảnh.');
      return;
    }
    setBusy(true);
    setError('');
    results.forEach((r) => revokeUrl(r.url));
    setResults([]);
    const next: ResultItem[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        next.push(await compressOne(files[i], fmt, maxBytes));
      }
      setResults(next);
    } catch (err: any) {
      next.forEach((r) => revokeUrl(r.url));
      setError((err && err.message) || 'Nén ảnh thất bại.');
    } finally {
      setBusy(false);
    }
  };

  const downloadAll = () => {
    results.forEach((r, i) => {
      setTimeout(() => downloadBlob(r.blob, r.name), i * 200);
    });
  };

  const large = files.some((f) => f.size > 20 * 1024 * 1024);

  return (
    <ToolLayout title="Nén ảnh">
      <p style={privacy}>
        Ảnh được nén hoàn toàn trên máy bạn (Canvas API), không gửi lên server language123.net.
      </p>
      <p style={warnBox}>
        JPEG/WebP dễ đạt mục tiêu dung lượng. PNG chủ yếu thu nhỏ kích thước — có thể không xuống dưới 1MB nếu ảnh
        rất lớn hoặc nhiều chi tiết.
      </p>

      <div style={card}>
        <label style={label}>1. Chọn ảnh (có thể nhiều file)</label>
        <p style={hint}>PNG / JPG / WebP / GIF (khung đầu) / BMP / SVG.</p>
        <FileDropZone
          accept="image/*,.svg"
          multiple
          hintText="Kéo thả hoặc bấm để chọn ảnh cần nén"
          onFiles={applyFiles}
        />
        {files.length ? (
          <p style={{ ...hint, marginTop: 8, marginBottom: 0 }}>
            {files.length} file · tổng {formatMb(files.reduce((s, f) => s + f.size, 0))} MB
          </p>
        ) : null}
        {large ? <p style={{ ...warnBox, marginTop: 12 }}>Một số file &gt; 20MB — trình duyệt có thể chậm.</p> : null}
      </div>

      <div style={card}>
        <label style={label}>2. Mục tiêu dung lượng</label>
        <div style={btnRow}>
          {TARGETS.map((t) => (
            <button key={t.id} type="button" style={chipStyle(targetId === t.id)} onClick={() => setTargetId(t.id)}>
              Dưới {t.label}
            </button>
          ))}
        </div>

        <label style={label}>Định dạng xuất</label>
        <div style={btnRow}>
          <button type="button" style={chipStyle(fmt === 'png')} onClick={() => setFmt('png')}>
            PNG
          </button>
          <button type="button" style={chipStyle(fmt === 'jpeg')} onClick={() => setFmt('jpeg')}>
            JPEG
          </button>
          <button type="button" style={chipStyle(fmt === 'webp')} onClick={() => setFmt('webp')}>
            WebP
          </button>
        </div>
        {fmt === 'webp' ? (
          <p style={hint}>Firefox có thể không encode WebP — tool sẽ tự fallback PNG.</p>
        ) : null}
        {fmt === 'png' ? (
          <p style={hint}>PNG không giảm chất lượng theo % — tool sẽ thu nhỏ kích thước để giảm dung lượng.</p>
        ) : null}
      </div>

      <div style={card}>
        <div style={btnRow}>
          <button type="button" style={primaryBtn} disabled={!files.length || busy} onClick={run}>
            {busy ? 'Đang nén…' : 'Nén ảnh'}
          </button>
          {results.length ? (
            <button type="button" style={secondaryBtn} onClick={downloadAll}>
              Tải tất cả
            </button>
          ) : null}
        </div>
        {error ? <p style={errorBox}>{error}</p> : null}
        {results.map((r) => {
          const ratio = r.beforeBytes > 0 ? Math.round((r.afterBytes / r.beforeBytes) * 100) : 100;
          return (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
                padding: 8,
                background: '#fafafa',
                borderRadius: 8,
              }}
            >
              <img
                src={r.url}
                alt={r.name}
                style={{ width: 64, height: 64, objectFit: 'contain', background: '#eee', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, wordBreak: 'break-all' }}>{r.name}</div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {formatKbOrMb(r.beforeBytes)} → {formatKbOrMb(r.afterBytes)} ({ratio}%)
                  {r.note ? ` · ${r.note}` : ''}
                </div>
              </div>
              <button type="button" style={primaryBtn} onClick={() => downloadBlob(r.blob, r.name)}>
                Tải
              </button>
            </div>
          );
        })}
      </div>
    </ToolLayout>
  );
};

export default CompressImage;
